import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Home, BookOpen, PlusCircle, Shield, LogOut, BarChart3, Search } from "lucide-react";
import { ARCHETYPES, APP_NAME, APP_TAGLINE, TC_LOGO_URL, CJ_AVATAR_URL } from "../lib/constants";
import { avatarSrc } from "../lib/api";
import api from "../lib/api";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import CommandPalette from "./CommandPalette";
import StreakFlame from "./StreakFlame";
import AiPausedBanner from "./AiPausedBanner";

function Logo() {
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group shrink-0">
      <img
        src={TC_LOGO_URL}
        alt="True Colors"
        data-testid="brand-tc-logo"
        className="hidden xl:block h-9 w-auto max-w-[160px] object-contain shrink-0"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <img
        src={CJ_AVATAR_URL}
        alt="CJ"
        className="h-9 w-9 rounded-full object-cover ring-2 ring-white shadow-sm border-l-0 shrink-0"
        data-testid="brand-cj-avatar"
      />
      <div className="leading-tight whitespace-nowrap shrink-0">
        <div className="font-display text-[16px] font-black tracking-tight text-tc-ink">{APP_NAME}</div>
        <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500 -mt-0.5 hidden xl:block">{APP_TAGLINE}</div>
      </div>
    </Link>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const archetype = ARCHETYPES[user?.archetype || "blue"];
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const lastCountRef = useRef(null); // tracks previous value across polls

  // Global keyboard shortcut: Cmd-K / Ctrl-K opens search
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user]);

  // Pending-stories notification, super-admin only. Poll every 30s and toast
  // whenever the count rises (a new story just landed in the queue).
  useEffect(() => {
    if (user?.role !== "super_admin") return;
    let active = true;
    const seenKey = `cj_last_seen_pending_${user.id}`;
    const lastSeen = parseInt(localStorage.getItem(seenKey) || "-1", 10);
    lastCountRef.current = lastSeen >= 0 ? lastSeen : null;

    const tick = async () => {
      try {
        const { data } = await api.get("/admin/pending-count");
        if (!active) return;
        const next = data?.count ?? 0;
        const prev = lastCountRef.current;
        if (prev !== null && next > prev) {
          toast.info(`New story waiting for review`, {
            description: `${next} pending in your queue. Tap to review.`,
            duration: 6000,
            action: { label: "Review", onClick: () => navigate("/admin") },
          });
        }
        lastCountRef.current = next;
        localStorage.setItem(seenKey, String(next));
        setPendingCount(next);
      } catch { /* silent — non-critical */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { active = false; clearInterval(id); };
  }, [user, navigate]);

  const navItems = [
    { to: "/", icon: Home, label: "Home", testid: "nav-home" },
    { to: "/library", icon: BookOpen, label: "Library", testid: "nav-library" },
    { to: "/capture", icon: PlusCircle, label: "Capture", testid: "nav-capture" },
    { to: "/impact", icon: BarChart3, label: "Impact", testid: "nav-impact" },
  ];
  if (user?.role === "super_admin") navItems.push({ to: "/admin", icon: Shield, label: "Admin", testid: "nav-admin", badge: pendingCount });

  return (
    <div className="min-h-screen flex flex-col bg-[var(--tc-bg-soft)]">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden md:flex items-center gap-1.5 shrink-0">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-bold transition-all ${
                    isActive
                      ? "bg-tc-ink text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                <it.icon size={16} />
                <span>{it.label}</span>
                {it.badge > 0 && (
                  <span
                    data-testid={`${it.testid}-badge`}
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center ring-2 ring-white"
                  >
                    {it.badge > 9 ? "9+" : it.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            {user && <StreakFlame />}
            {user && (
              <button
                onClick={() => setPaletteOpen(true)}
                data-testid="header-search"
                title="Search (⌘K)"
                className="hidden sm:inline-flex items-center gap-2 h-10 px-3 rounded-full border border-slate-200 bg-white hover:border-slate-300 text-slate-500 text-sm transition-colors"
              >
                <Search size={15} />
                <span className="hidden lg:inline">Search</span>
                <kbd className="hidden lg:inline-flex h-5 px-1.5 items-center rounded bg-slate-100 text-[10px] font-bold text-slate-500 tracking-wider">⌘K</kbd>
              </button>
            )}
            {user && (
              <button
                onClick={() => setPaletteOpen(true)}
                data-testid="header-search-mobile"
                title="Search"
                className="sm:hidden w-10 h-10 rounded-full border border-slate-200 bg-white inline-flex items-center justify-center text-slate-500"
              >
                <Search size={16} />
              </button>
            )}
            {user && (
              <div className="flex items-center gap-2">
                <Link
                  to="/stories"
                  className="w-10 h-10 rounded-full bg-cover bg-center border-2 hover:scale-105 transition-transform cursor-pointer"
                  style={{ backgroundImage: `url(${avatarSrc(user)})`, borderColor: archetype.color }}
                  data-testid="header-avatar"
                  title="Click to change your photo"
                />
                <button onClick={logout} data-testid="logout-button" className="text-slate-500 hover:text-slate-900 p-2 rounded-full hover:bg-slate-100">
                  <LogOut size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
        {user && <AiPausedBanner />}
      </header>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      <main className="flex-1 w-full pb-24 md:pb-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 120, damping: 18 }}>
          {children}
        </motion.div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-3 left-3 right-3 z-50 rounded-3xl border border-slate-200 bg-white/95 backdrop-blur shadow-lg">
        <div className="flex items-center justify-around px-2 py-2">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`mobile-${it.testid}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-2xl text-[11px] font-bold transition-colors ${
                  isActive ? "bg-tc-ink text-white" : "text-slate-500"
                }`
              }
            >
              <it.icon size={18} />
              <span>{it.label}</span>
              {it.badge > 0 && (
                <span
                  data-testid={`mobile-${it.testid}-badge`}
                  className="absolute top-0.5 right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-extrabold flex items-center justify-center ring-2 ring-white"
                >
                  {it.badge > 9 ? "9+" : it.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
